import { Badge, Button, Label, Typography } from "@/components/ui";
import PurifiedHtml from "@/components/ui/PurifiedHtml/PurifiedHtml";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/shadcn/Accordion/Accordion";
import { type ContactCommunicationEmail } from "@/lib/types/Contact";
import { convertToLocalFromDatabaseDateTime } from "@/lib/utils/dateUtils";
import { formatDateTime } from "@/lib/utils/format";
import { ContactEmailIcon } from "@/lib/utils/iconUtils";
import pluralize from "pluralize";
import { useMemo, useState } from "react";

interface ContactEmailCommunicationSectionProps {
  communications?: ContactCommunicationEmail[];
}

const directionBadgeClassMap: Record<
  NonNullable<ContactCommunicationEmail["direction"]>,
  string
> = {
  OUTBOUND: "bg-primary/10 text-primary border border-primary/20",
  INBOUND: "bg-emerald-100 text-emerald-800 border border-emerald-200",
};

const formatEmailList = (emails?: string[]) => {
  if (!emails || emails.length === 0) return undefined;
  return emails.join(", ");
};

export default function ContactEmailCommunicationSection({
  communications,
}: ContactEmailCommunicationSectionProps) {
  const communicationsToDisplay = [...(communications ?? [])].sort((a, b) => {
    const aDate = a.dateCreated || a.dateModified;
    const bDate = b.dateCreated || b.dateModified;

    if (!aDate && !bDate) return 0;
    if (!aDate) return 1;
    if (!bDate) return -1;

    return new Date(bDate).getTime() - new Date(aDate).getTime();
  });

  const communicationsCount = communicationsToDisplay.length;
  const PAGE_SIZE = 10;
  const pageCount = Math.ceil(communicationsCount / PAGE_SIZE);
  const [page, setPage] = useState(0);

  const pagedCommunications = useMemo(() => {
    const start = page * PAGE_SIZE;
    return communicationsToDisplay.slice(start, start + PAGE_SIZE);
  }, [communicationsToDisplay, page]);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-2">
        <ContactEmailIcon className="text-muted-foreground h-5 w-5" />
        <div className="flex items-baseline gap-2">
          <Label className="text-lg font-semibold">Email communications</Label>
          <Typography
            size="sm"
            variant="subHeading"
            className="text-muted-foreground"
          >
            {pluralize("email", communicationsCount, true)}
          </Typography>
        </div>
      </div>

      {communicationsCount > 0 ? (
        <div className="grid gap-3">
          {pagedCommunications.map((communication) => {
            const createdAt = communication.dateCreated
              ? formatDateTime(
                  convertToLocalFromDatabaseDateTime(communication.dateCreated),
                  false,
                )
              : communication.dateModified
                ? formatDateTime(
                    convertToLocalFromDatabaseDateTime(
                      communication.dateModified,
                    ),
                    false,
                  )
                : undefined;

            const toEmails = formatEmailList(
              communication.toEmails ?? undefined,
            );
            const ccEmails = formatEmailList(
              communication.ccEmails ?? undefined,
            );
            const bccEmails = formatEmailList(
              communication.bccEmails ?? undefined,
            );

            return (
              <Accordion
                key={communication.id}
                type="single"
                collapsible
                className="bg-card overflow-hidden rounded-md border"
              >
                <AccordionItem value="email-detail" className="border-none">
                  <AccordionTrigger className="bg-card flex items-center justify-between gap-2 px-4 py-3 text-left text-sm font-semibold hover:no-underline">
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge
                        className={
                          directionBadgeClassMap[
                            communication.direction ?? "INBOUND"
                          ]
                        }
                      >
                        {communication.direction === "OUTBOUND"
                          ? "Sent"
                          : "Received"}
                      </Badge>
                      {communication.subject ? (
                        <Typography className="font-semibold">
                          {communication.subject}
                        </Typography>
                      ) : (
                        <Typography className="text-muted-foreground">
                          (No subject)
                        </Typography>
                      )}
                    </div>
                    {createdAt && (
                      <Typography
                        size="sm"
                        variant="subHeading"
                        className="text-muted-foreground"
                      >
                        {createdAt}
                      </Typography>
                    )}
                  </AccordionTrigger>
                  <AccordionContent className="bg-card px-4">
                    <div className="flex flex-col gap-3 py-3">
                      <div className="text-sm">
                        <Typography
                          size="sm"
                          variant="subHeading"
                          className="text-muted-foreground"
                        >
                          From:
                        </Typography>
                        <Typography className="break-words">
                          {communication.fromEmail ?? "-"}
                        </Typography>
                      </div>

                      {toEmails && (
                        <div className="text-sm">
                          <Typography
                            size="sm"
                            variant="subHeading"
                            className="text-muted-foreground"
                          >
                            To:
                          </Typography>
                          <Typography className="break-words">
                            {toEmails}
                          </Typography>
                        </div>
                      )}

                      {ccEmails && (
                        <div className="text-sm">
                          <Typography
                            size="sm"
                            variant="subHeading"
                            className="text-muted-foreground"
                          >
                            CC:
                          </Typography>
                          <Typography className="break-words">
                            {ccEmails}
                          </Typography>
                        </div>
                      )}

                      {bccEmails && (
                        <div className="text-sm">
                          <Typography
                            size="sm"
                            variant="subHeading"
                            className="text-muted-foreground"
                          >
                            BCC:
                          </Typography>
                          <Typography className="break-words">
                            {bccEmails}
                          </Typography>
                        </div>
                      )}

                      <PurifiedHtml
                        html={communication.bodyHtml}
                        fallback={
                          communication.bodyText ? (
                            <Typography className="whitespace-pre-wrap">
                              {communication.bodyText}
                            </Typography>
                          ) : undefined
                        }
                      />
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            );
          })}
        </div>
      ) : (
        <Typography className="text-muted-foreground" size="sm">
          No email communications yet.
        </Typography>
      )}
      {pageCount > 1 && (
        <div className="flex items-center justify-end gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setPage((prev) => Math.max(prev - 1, 0));
            }}
            disabled={page === 0}
          >
            Previous
          </Button>
          <Typography size="sm" className="text-muted-foreground">
            Page {page + 1} of {pageCount}
          </Typography>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setPage((prev) => Math.min(prev + 1, pageCount - 1));
            }}
            disabled={page === pageCount - 1}
          >
            Next
          </Button>
        </div>
      )}
    </div>
  );
}
